import { useState } from 'react';
import { costCentersApi, fixedAssetsApi } from '../../api/services';
import type { AssetCategoryDto, DepreciationLineDto, DepreciationRunDto, FixedAssetDto, SaveAssetCategoryDto, SaveFixedAssetDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Stat, Table, Tabs, fmtDate, fmtDateTime, fmtMoney, fmtNumber, money, todayIso, useAction, useAsync,
} from '../../ui/components';
import { AccountPicker } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { Link } from '../../router';
import { PERM, useCan } from '../../ui/access';

type TabKey = 'assets' | 'depreciation' | 'categories';
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/** Activos fijos: registro, depreciación mensual por línea recta y bajas. */
export function FixedAssetsPage() {
  const [tab, setTab] = useState<TabKey>('assets');
  const categories = useAsync(() => fixedAssetsApi.categories(), []);
  return (
    <>
      <PageHeader title="Activos fijos" subtitle="Registro de activos, depreciación mensual y bajas" />
      <PageHelp page="fixedAssets" />
      <Tabs<TabKey> tabs={[{ key: 'assets', label: 'Activos' }, { key: 'depreciation', label: 'Depreciación mensual' }, { key: 'categories', label: 'Categorías' }]} active={tab} onChange={setTab} />
      {tab === 'assets' && <AssetsTab categories={categories.data ?? []} goCategories={() => setTab('categories')} />}
      {tab === 'depreciation' && <DepreciationTab />}
      {tab === 'categories' && <CategoriesTab state={categories} />}
    </>
  );
}

function AssetsTab({ categories, goCategories }: { categories: AssetCategoryDto[]; goCategories: () => void }) {
  const [status, setStatus] = useState<'' | 'A' | 'B'>('A');
  const assets = useAsync(() => fixedAssetsApi.list(status || undefined), [status]);
  const canEdit = useCan(PERM.setup);
  const canClose = useCan(PERM.periodClose);
  const [editing, setEditing] = useState<FixedAssetDto | 'new' | null>(null);
  const [disposing, setDisposing] = useState<FixedAssetDto | null>(null);
  const { run, busy } = useAction();
  return (
    <>
      <div className="acc-toolbar">
        <Field label="Estado"><Select value={status} onChange={(v) => setStatus(v)} options={[{ value: 'A', label: 'Activos' }, { value: 'B', label: 'Dados de baja' }]} placeholder="Todos" /></Field>
        {canEdit && <Button kind="primary" disabled={categories.length === 0} onClick={() => setEditing('new')}>Nuevo activo</Button>}
        <Button busy={busy} onClick={() => run(() => fixedAssetsApi.exportList())}>Exportar CSV</Button>
      </div>
      {categories.length === 0 && <Alert kind="info">Primero cree al menos una categoría (con sus cuentas contables). <Button kind="link" onClick={goCategories}>Ir a Categorías</Button></Alert>}
      <Loadable state={assets}>
        {(rows) => (
          <Table<FixedAssetDto> rows={rows} rowKey={(a) => a.id} empty="No hay activos." columns={[
            { key: 'c', title: 'Código', render: (a) => a.code },
            { key: 'd', title: 'Descripción', render: (a) => <>{a.description}<div className="acc-muted">{a.categoryName}{a.location ? ` · ${a.location}` : ''}</div></> },
            { key: 'f', title: 'Adquisición', render: (a) => fmtDate(a.acquisitionDate) },
            money<FixedAssetDto>('co', 'Costo', (a) => a.cost),
            money<FixedAssetDto>('m', 'Cuota mensual', (a) => a.monthlyDepreciation),
            money<FixedAssetDto>('ac', 'Dep. acumulada', (a) => a.accumulatedDepreciation),
            money<FixedAssetDto>('v', 'Valor en libros', (a) => a.bookValue),
            { key: 's', title: 'Estado', render: (a) => (a.status === 'A' ? <Badge tone="ok">Activo</Badge> : <><Badge>Baja {fmtDate(a.disposalDate)}</Badge>{a.disposalEntryCode && <div><Link to={`/entries/${a.disposalEntryCode}`}>Asiento {a.disposalEntryCode}</Link></div>}</>) },
            { key: 'x', title: '', render: (a) => (
              <>
                {canEdit && a.status === 'A' && <Button kind="link" onClick={() => setEditing(a)}>Editar…</Button>}
                {canClose && a.status === 'A' && <Button kind="link" onClick={() => setDisposing(a)}>Dar de baja…</Button>}
                {canClose && a.status === 'B' && <Button kind="link" busy={busy} onClick={() => run(async () => { await fixedAssetsApi.cancelDisposal(a.id); assets.reload(); },
                  { confirm: `Anular la baja de ${a.code}? Se registra el reverso del asiento de la baja.`, danger: true, success: 'Baja anulada.' })}>Anular baja…</Button>}
              </>
            ) },
          ]} />
        )}
      </Loadable>
      {editing && <AssetModal asset={editing === 'new' ? null : editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); assets.reload(); }} />}
      {disposing && <DisposeModal asset={disposing} onClose={() => setDisposing(null)} onSaved={() => { setDisposing(null); assets.reload(); }} />}
    </>
  );
}

function AssetModal({ asset, categories, onClose, onSaved }: { asset: FixedAssetDto | null; categories: AssetCategoryDto[]; onClose: () => void; onSaved: () => void }) {
  const centers = useAsync(() => costCentersApi.list().catch(() => []), []);
  const first = categories.find((c) => c.status === 'A');
  const [f, setF] = useState<SaveFixedAssetDto>({
    code: asset?.code ?? '', description: asset?.description ?? '', categoryId: asset?.categoryId ?? first?.id ?? 0,
    acquisitionDate: asset?.acquisitionDate.slice(0, 10) ?? todayIso(), depreciationStart: asset?.depreciationStart.slice(0, 10) ?? todayIso(),
    cost: asset?.cost ?? 0, residualValue: asset?.residualValue ?? 0, usefulLifeMonths: asset?.usefulLifeMonths ?? null, initialDepreciation: asset?.initialDepreciation ?? 0,
    costCenterId: asset?.costCenterId ?? null, location: asset?.location ?? '', responsible: asset?.responsible ?? '', serial: asset?.serial ?? '', purchaseDocument: asset?.purchaseDocument ?? '',
  });
  const locked = !!asset?.hasDepreciation;
  const category = categories.find((c) => c.id === f.categoryId);
  const life = f.usefulLifeMonths ?? category?.usefulLifeMonths ?? 0;
  const monthly = life > 0 ? (f.cost - f.residualValue - f.initialDepreciation) / life : 0;
  const { run, busy, fieldErrors } = useAction();
  return (
    <Modal title={asset ? `Editar ${asset.code}` : 'Nuevo activo'} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => {
        const dto = { ...f, location: f.location || null, responsible: f.responsible || null, serial: f.serial || null, purchaseDocument: f.purchaseDocument || null };
        if (asset) await fixedAssetsApi.update(asset.id, dto); else await fixedAssetsApi.create(dto);
        onSaved();
      }, { success: asset ? 'Activo actualizado.' : 'Activo registrado.' })}>
        {locked && <Alert kind="info">El activo ya tiene depreciación registrada: los montos, la categoría y las fechas no se pueden cambiar.</Alert>}
        <div className="acc-grid-form">
          <Field label="Código" error={fieldErrors.Code}><Input value={f.code} onChange={(v) => setF({ ...f, code: v })} maxLength={30} required /></Field>
          <Field label="Descripción" wide error={fieldErrors.Description}><Input value={f.description} onChange={(v) => setF({ ...f, description: v })} maxLength={200} required /></Field>
          <Field label="Categoría"><Select value={f.categoryId} onChange={(v) => setF({ ...f, categoryId: v })} disabled={locked} options={categories.filter((c) => c.status === 'A' || c.id === f.categoryId).map((c) => ({ value: c.id, label: c.name }))} /></Field>
          <Field label="Fecha de adquisición"><Input type="date" value={f.acquisitionDate} onChange={(v) => setF({ ...f, acquisitionDate: v })} disabled={locked} required /></Field>
          <Field label="Inicio de depreciación" hint="Primer mes que se deprecia"><Input type="date" value={f.depreciationStart} onChange={(v) => setF({ ...f, depreciationStart: v })} disabled={locked} required /></Field>
          <Field label="Costo" error={fieldErrors.Cost}><NumberInput value={f.cost} min={0} onChange={(v) => setF({ ...f, cost: v })} disabled={locked} required /></Field>
          <Field label="Valor residual"><NumberInput value={f.residualValue} min={0} onChange={(v) => setF({ ...f, residualValue: v })} disabled={locked} /></Field>
          <Field label="Vida útil (meses)" hint={`Vacío = la de la categoría (${category?.usefulLifeMonths ?? '-'})`}><NumberInput step="1" min={1} value={f.usefulLifeMonths} onChange={(v) => setF({ ...f, usefulLifeMonths: v || null })} disabled={locked} /></Field>
          <Field label="Depreciación acumulada inicial" hint="Sólo si el activo ya venía depreciándose antes de usar el sistema; la vida útil es entonces la restante"><NumberInput value={f.initialDepreciation} min={0} onChange={(v) => setF({ ...f, initialDepreciation: v })} disabled={locked} /></Field>
          {(centers.data ?? []).length > 0 && (
            <Field label="Centro de costo"><Select value={f.costCenterId ?? ''} onChange={(v) => setF({ ...f, costCenterId: v || null })} placeholder="Sin centro" options={(centers.data ?? []).filter((c) => c.status === 'A').map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))} /></Field>
          )}
          <Field label="Ubicación"><Input value={f.location ?? ''} onChange={(v) => setF({ ...f, location: v })} maxLength={100} /></Field>
          <Field label="Responsable"><Input value={f.responsible ?? ''} onChange={(v) => setF({ ...f, responsible: v })} maxLength={100} /></Field>
          <Field label="Serie / placa"><Input value={f.serial ?? ''} onChange={(v) => setF({ ...f, serial: v })} maxLength={100} /></Field>
          <Field label="Documento de compra"><Input value={f.purchaseDocument ?? ''} onChange={(v) => setF({ ...f, purchaseDocument: v })} maxLength={100} /></Field>
        </div>
        <p className="acc-muted">Cuota mensual aproximada: <strong>{fmtMoney(Math.max(0, monthly))}</strong>. Registrar el activo no genera asiento: la compra se registra como siempre (factura de proveedor o asiento).</p>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

function DisposeModal({ asset, onClose, onSaved }: { asset: FixedAssetDto; onClose: () => void; onSaved: () => void }) {
  const [date, setDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [account, setAccount] = useState('');
  const { run, busy } = useAction();
  return (
    <Modal title={`Dar de baja ${asset.code}`} onClose={onClose}>
      <Form onSubmit={() => run(async () => { await fixedAssetsApi.dispose(asset.id, date, reason.trim(), account); onSaved(); },
        { confirm: 'Se registrará el asiento de la baja. ¿Continuar?', danger: true, success: 'Activo dado de baja.' })}>
        <dl className="acc-dl">
          <dt>Costo</dt><dd>{fmtMoney(asset.cost)}</dd>
          <dt>Depreciación acumulada</dt><dd>{fmtMoney(asset.accumulatedDepreciation)}</dd>
          <dt>Valor en libros</dt><dd><strong>{fmtMoney(asset.bookValue)}</strong></dd>
        </dl>
        <Alert kind="info">Antes de la baja, registre la depreciación hasta el mes anterior. El asiento acredita la cuenta del activo por el costo, debita la depreciación acumulada y lleva el valor en libros a la cuenta que elija (pérdida por baja de activos, o la cuenta por cobrar si lo vendió).</Alert>
        <div className="acc-grid-form">
          <Field label="Fecha de baja"><Input type="date" value={date} onChange={setDate} required /></Field>
          <Field label="Cuenta para el valor en libros"><AccountPicker value={account} onChange={setAccount} /></Field>
          <Field label="Motivo" wide><Input value={reason} onChange={setReason} maxLength={200} required /></Field>
        </div>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="danger" type="submit" busy={busy} disabled={!account || reason.trim().length < 5}>Dar de baja</Button></div>
      </Form>
    </Modal>
  );
}

function DepreciationTab() {
  const runs = useAsync(() => fixedAssetsApi.runs(), []);
  const canClose = useCan(PERM.periodClose);
  const now = new Date();
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [asked, setAsked] = useState<{ year: number; month: number } | null>(null);
  const preview = useAsync(() => fixedAssetsApi.preview(asked!.year, asked!.month), [asked?.year, asked?.month], asked !== null);
  const { run, busy } = useAction();
  return (
    <>
      {canClose && (
        <Card title="Registrar la depreciación de un mes">
          <div className="acc-toolbar">
            <Field label="Año"><NumberInput step="1" value={year} onChange={(v) => setYear(v || now.getFullYear())} /></Field>
            <Field label="Mes"><Select value={month} onChange={setMonth} options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} /></Field>
            <Button onClick={() => setAsked({ year, month })}>Revisar</Button>
          </div>
          {asked && (
            <Loadable state={preview}>
              {(p) => (
                <>
                  {p.blockers.map((b) => <Alert key={b} kind="error">{b}</Alert>)}
                  {p.warnings.map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
                  {p.lines.length > 0 && (
                    <>
                      <div className="acc-stats"><Stat label="Depreciación del mes" value={fmtMoney(p.total)} /></div>
                      <Table<DepreciationLineDto> rows={p.lines} rowKey={(l) => l.assetId} columns={[
                        { key: 'c', title: 'Activo', render: (l) => `${l.assetCode} · ${l.description}` },
                        { key: 'g', title: 'Categoría', render: (l) => l.categoryName },
                        money<DepreciationLineDto>('m', 'Depreciación', (l) => l.amount),
                        money<DepreciationLineDto>('a', 'Acumulada después', (l) => l.accumulatedAfter),
                        money<DepreciationLineDto>('v', 'Valor en libros después', (l) => l.bookValueAfter),
                      ]} />
                    </>
                  )}
                  <div className="acc-actions">
                    <Button kind="primary" disabled={!p.canPost} busy={busy} onClick={() => run(async () => { await fixedAssetsApi.post(asked.year, asked.month); setAsked(null); runs.reload(); },
                      { confirm: `Registrar la depreciación de ${MONTHS[asked.month - 1].toLowerCase()} ${asked.year}?`, success: 'Depreciación registrada.' })}>Registrar depreciación</Button>
                  </div>
                </>
              )}
            </Loadable>
          )}
        </Card>
      )}
      <Loadable state={runs}>
        {(rows) => (
          <Table<DepreciationRunDto> rows={rows} rowKey={(r) => r.id} empty="Todavía no hay depreciaciones registradas." columns={[
            { key: 'm', title: 'Mes', render: (r) => `${MONTHS[Number(r.month.slice(5, 7)) - 1]} ${r.month.slice(0, 4)}` },
            money<DepreciationRunDto>('t', 'Total', (r) => r.total),
            { key: 'a', title: 'Asiento', render: (r) => <Link to={`/entries/${r.entryCode}`}>{r.entryCode}</Link> },
            { key: 's', title: 'Estado', render: (r) => (r.status === 'A' ? <Badge tone="ok">Vigente</Badge> : <Badge tone="bad">Anulada</Badge>) },
            { key: 'f', title: 'Registrada', render: (r) => fmtDateTime(r.createdOn) },
            { key: 'x', title: '', render: (r) => (canClose && r.status === 'A' ? <Button kind="link" busy={busy} onClick={() => run(async () => { await fixedAssetsApi.cancelRun(r.id); runs.reload(); },
              { confirm: 'Anular esta depreciación? Se registra su reverso. Sólo se puede anular la más reciente.', danger: true, success: 'Depreciación anulada.' })}>Anular…</Button> : null) },
          ]} />
        )}
      </Loadable>
    </>
  );
}

function CategoriesTab({ state }: { state: { data?: AssetCategoryDto[]; loading: boolean; error: unknown; reload: () => void } }) {
  const canEdit = useCan(PERM.setup);
  const [editing, setEditing] = useState<AssetCategoryDto | 'new' | null>(null);
  return (
    <>
      {canEdit && <div className="acc-actions" style={{ justifyContent: 'flex-start' }}><Button kind="primary" onClick={() => setEditing('new')}>Nueva categoría</Button></div>}
      <Loadable state={state}>
        {(rows) => (
          <Table<AssetCategoryDto> rows={rows} rowKey={(c) => c.id} empty="No hay categorías." columns={[
            { key: 'n', title: 'Categoría', render: (c) => <strong>{c.name}</strong> },
            { key: 'v', title: 'Vida útil', render: (c) => `${c.usefulLifeMonths} meses (${fmtNumber(c.usefulLifeMonths / 12, 1)} años)` },
            { key: 'a', title: 'Cuentas', render: (c) => <span className="acc-muted">Activo {c.assetAccount} · Acumulada {c.accumulatedAccount} · Gasto {c.expenseAccount}</span> },
            { key: 'q', title: 'Activos', align: 'right', render: (c) => c.assets },
            { key: 's', title: 'Estado', render: (c) => (c.status === 'A' ? <Badge tone="ok">Activa</Badge> : <Badge>Inactiva</Badge>) },
            { key: 'x', title: '', render: (c) => (canEdit ? <Button kind="link" onClick={() => setEditing(c)}>Editar…</Button> : null) },
          ]} />
        )}
      </Loadable>
      {editing && <CategoryModal category={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); state.reload(); }} />}
    </>
  );
}

function CategoryModal({ category, onClose, onSaved }: { category: AssetCategoryDto | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<SaveAssetCategoryDto>({
    name: category?.name ?? '', usefulLifeMonths: category?.usefulLifeMonths ?? 60, assetAccount: category?.assetAccount ?? '',
    accumulatedAccount: category?.accumulatedAccount ?? '', expenseAccount: category?.expenseAccount ?? '', status: category?.status ?? 'A',
  });
  const { run, busy, fieldErrors } = useAction();
  return (
    <Modal title={category ? `Editar ${category.name}` : 'Nueva categoría'} onClose={onClose}>
      <Form onSubmit={() => run(async () => { if (category) await fixedAssetsApi.updateCategory(category.id, f); else await fixedAssetsApi.createCategory(f); onSaved(); },
        { success: 'Categoría guardada.' })}>
        <div className="acc-grid-form">
          <Field label="Nombre" error={fieldErrors.Name}><Input value={f.name} onChange={(v) => setF({ ...f, name: v })} maxLength={100} required /></Field>
          <Field label="Vida útil (meses)" hint="Ej.: 60 = 5 años. Consulte la tabla de su país."><NumberInput step="1" min={1} value={f.usefulLifeMonths} onChange={(v) => setF({ ...f, usefulLifeMonths: v })} /></Field>
          <Field label="Estado"><Select value={f.status} onChange={(v) => setF({ ...f, status: v })} options={[{ value: 'A', label: 'Activa' }, { value: 'I', label: 'Inactiva' }]} /></Field>
          <Field label="Cuenta del activo"><AccountPicker value={f.assetAccount} onChange={(v) => setF({ ...f, assetAccount: v })} /></Field>
          <Field label="Cuenta de depreciación acumulada"><AccountPicker value={f.accumulatedAccount} onChange={(v) => setF({ ...f, accumulatedAccount: v })} /></Field>
          <Field label="Cuenta de gasto por depreciación"><AccountPicker value={f.expenseAccount} onChange={(v) => setF({ ...f, expenseAccount: v })} /></Field>
        </div>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}
